/* eslint-disable max-lines */
import _ from 'lodash';
import type { IObservableArray } from 'mobx';
import { observable, computed, action } from 'mobx';

import type { ArrangementRequestLimitTypeJson } from '@this/domain/organization/arrangement_request_limit_type';
import ArrangementRequestLimitType from '@this/domain/organization/arrangement_request_limit_type';
import type ExicCard from '@this/src/domain/exic_card';
import RailwayPackageLimitType from '@this/domain/railway_package_limit_type';
import type { RailwayPackageLimitTypeJson } from '@this/domain/railway_package_limit_type/railway_package_limit_type';
import type { PartialNullable } from '@this/src/util/type';
import User from '../user/user';
import type UserJson from '../user/user_json';
import type { ShinkansenLimitTypeJson } from './shinkansen_limit_type';
import ShinkansenLimitType from './shinkansen_limit_type';
import type { DomesticAirLimitTypeJson } from './domestic_air_limit_type';
import DomesticAirLimitType from './domestic_air_limit_type';
import type { RentalCarLimitTypeJson } from './rental_car_limit_type';
import RentalCarLimitType from './rental_car_limit_type';
import type { PackageLimitTypeJson } from './package_limit_type';
import PackageLimitType from './package_limit_type';
import type { HotelLimitTypeJson } from './hotel_limit_type';
import HotelLimitType from './hotel_limit_type';
import MarginType from './margin_type2';
import type { BulkTicketArgs } from '../bulk_ticket2';
import BulkTicket from '../bulk_ticket2';
import type { FreeeCompanyArgs } from '../freee_company2';
import FreeeCompany from '../freee_company2';
import type { OrganizationBillReceiverArgs } from './organization_bill_receiver2';
import OrganizationBillReceiver from './organization_bill_receiver2';
import type { ArrangementRequestSettingJson } from './arrangement_request_setting2';
import ArrangementRequestSetting from './arrangement_request_setting2';
import type { InfomartApiTokenArgs } from './infomart_api_token';
import InfomartApiToken from './infomart_api_token';
import type { InvoiceSettingArgs } from './invoice_setting';
import InvoiceSetting from './invoice_setting';
import type {
  OrganizationSystemFeeSettingJson,
  OrganizationSystemFeeSettingPaymentMethodType
} from './organization_system_fee_setting';
import { OrganizationSystemFeeSetting } from './organization_system_fee_setting';
import type { AutoRemoveFreeePostDeals, SettingArgs } from '../setting';
import Setting from '../setting';
import type { GradeArgs } from '../grade';
import OptionPlanPrice from '../plan/option_plan_price';
import type { OptionPlanPriceArgs } from '../plan/option_plan_price';
import type { SavingAccountArgs } from '../saving_accounts/saving_account';
import SavingAccount from '../saving_accounts/saving_account';

export type PaymentMethodOptions = 'invoice' | 'card' | 'selectable';
export type PaymentMethodType = 'billing' | 'card';
export type PaymentGatewayType = 'gmo' | 'sony';
export type ReserveRequestMailNotificationType = 'togglable' | 'selectable_user';
export type TransitSearchApi = 'legacy' | 'ekispert';
export type TransportElementReservationApi = 'smart_ticket' | 'ticket_opr';
export type ExicUsageMode = 'card_based' | 'user_based';
export type AllowanceTargetType = 'all' | 'hotel_only';
export type FobiddenRepository = {
  category: string;
  repository: string;
};
export type ForbiddenRequestForm = {
  category: string;
};
export type FareOption = {
  optionType: 'corporate';
  qualifier: string;
  identity: string;
};
export type FareOptionArgs = {
  option_type: 'corporate';
  qualifier: string;
  identity: string;
};
export type workflowStyle = 'department' | 'trip';
export type TripReportWorkflowStyle = 'department' | 'project' | 'trip_report';
export type DefaultShinkansenAddressType = 'select' | 'text';
type freeePartnerSetting = 'partner' | 'user_name';
type InvoiceSplitType = 'none' | 'charging_department' | 'project' | 'invoice_split_target';
export type WorkflowType = 'selected_amount' | 'with_buffer' | 'price_limit';

interface revenueReferenceDateTypesInterface {
  [key: string]: string;
}

export type OrganizationArgs = PartialNullable<{
  domain: string | null;
  members: UserJson[];
  margin_types: MarginType[];
  bulk_tickets: BulkTicketArgs[];
  selected_freee_company_id: number | null;
  freee_companies: FreeeCompanyArgs[];
  freee_partner_id: number | null;
  freee_monthly_price_section_id: number;
  freee_project_destination: string;
  is_create_freee_tag: boolean;
  freee_department: string;
  sso_entity_id: string;
  sso_url: string;
  sso_logout_url: string;
  sso_idp_cert: string;
  amadeus_company_name: string;
  person_in_charge_name: string;
  person_in_charge_email: string;
  billing_email: string;
  bank_account_info: string;
  agency_bank_account_info: string;
  member_count: number;
  admin_count: number;
  enable_count: number;
  disable_count: number;
  invite_count: number;
  uninvite_count: number;
  latest_plan_name: string;
  organization_bill_receivers: OrganizationBillReceiverArgs[];
  plan_id: number;
  salesforce_id: string;
  arrangement_request_setting: ArrangementRequestSettingJson;
  infomart_api_token: InfomartApiTokenArgs;
  immigration_card_enabled: boolean;
  visa_application_enabled: boolean;
  salesforce_import_id: string;
  enterprise_identification_number: string;
  show_ex: boolean;
  use_non_order_item: boolean;
  request_form_approval_required: boolean;
  invoice_day: number;
  setting: SettingArgs;
  admins: {
    name: string;
    email: string;
  }[];
  invoice_setting: InvoiceSettingArgs;
  grades: GradeArgs[];
  exic_cards: ExicCard[];
  forbidden_repositories: FobiddenRepository[];
  forbidden_request_forms: ForbiddenRequestForm[];
  fare_options: FareOptionArgs[];
  segment1: string;
  segment2: string;
  company_size: string;
  exic_basic_member_no: string;
  expenses_multistage_approval: boolean;
  workflow_style: workflowStyle;
  trip_report_workflow_style: TripReportWorkflowStyle;
  is_own_company: boolean;
  use_qr: boolean;
  use_chatbot: boolean;
  use_slack_app: boolean;
  user_dashboard_label1: string;
  user_dashboard_label2: string;
  user_dashboard_label3: string;
  system_fee_setting: OrganizationSystemFeeSettingJson;
  freee_partner_setting: freeePartnerSetting;
  invoice_split_type: InvoiceSplitType;
  current_option_plan_prices: OptionPlanPriceArgs[];
  saving_account_id: number;
  allow_overdraft: boolean;
  use_exic_reference_number: boolean;
  saving_account: SavingAccountArgs;
  infomart_private_cust_cd: string;
  infomart_transfer_code: string;
  api_authenticate_key: string;
  domestic_hotel_under_regulation_workflow_type: WorkflowType;
  domestic_hotel_under_regulation_buffer_amount: number;
  domestic_hotel_over_regulation_workflow_type: WorkflowType;
  domestic_hotel_over_regulation_buffer_amount: number;
  foreign_hotel_under_regulation_workflow_type: WorkflowType;
  foreign_hotel_under_regulation_buffer_amount: number;
  foreign_hotel_over_regulation_workflow_type: WorkflowType;
  foreign_hotel_over_regulation_buffer_amount: number;
  domestic_flight_workflow_type: WorkflowType;
  domestic_flight_buffer_amount: number;
  package_workflow_type: WorkflowType;
  package_buffer_amount: number;
  concur_client_id: string;
  concur_client_secret?: string;
  concur_expense_enabled?: boolean;
  concur_expense_type_id?: string;
  concur_payment_type_id?: string;
}> & {
  id: number;
  service_id: number | string;
  name: string;
  url: string | null;
  tel: string;
  address: string;
  sso_enabled: boolean;
  orientation: 'trip' | 'expenses';
  rakuten_inner_fee_rate: number;
  exic_agency_fee_rate: number;
  business_ekinet_corporate_inquiry_number: string;
  business_ekinet_agency_fee_rate: number;
  enabled_customize_address: boolean;
  use_bulk_ticket: boolean;
  use_kyuusyuu_ticket: boolean;
  include_tax: boolean;
  hotel_sort_type: boolean | 0 | 1;
  enable_auto_reserve_rakuten_hotel: boolean;
  show_fee: boolean;
  shinkansen_limit_type?: ShinkansenLimitTypeJson;
  domestic_air_limit_type?: DomesticAirLimitTypeJson;
  rental_car_limit_type: RentalCarLimitTypeJson;
  package_limit_type?: PackageLimitTypeJson;
  railway_package_limit_type?: RailwayPackageLimitTypeJson;
  hotel_limit_type?: HotelLimitTypeJson;
  arrangement_request_limit_type?: ArrangementRequestLimitTypeJson;
  revenue_reference_date_type: 'confirmed_at' | 'end_date';
  payment_method_type: PaymentMethodOptions;
  payment_gateway_type: PaymentGatewayType | null;
  reserve_request_mail_notification_type: ReserveRequestMailNotificationType;
  freee_workflow: boolean;
  freee_workflow_approval_flow_route_id_domestic: number | null;
  freee_workflow_form_id_domestic: number | null;
  freee_workflow_approval_flow_route_id_foreign: number | null;
  freee_workflow_form_id_foreign: number | null;
  default_shinkansen_address_type: DefaultShinkansenAddressType;
  transit_search_api: TransitSearchApi;
  ekispert_display_limited: boolean;
  enable_rentalcar_form: boolean;
  day_off_operation: boolean;
  enable_auto_stock_check: boolean;
  enable_batch_approval: boolean;
  use_smart_search: boolean;
  use_chatbot: boolean;
  use_slack_app: boolean;
  transport_element_reservation_api: TransportElementReservationApi;
  system_fee_setting?: OrganizationSystemFeeSettingJson;
  enable_just_before_reserve_expedia_hotel: boolean;
  enable_multiple_hotel_plans: boolean;
  freee_partner_setting: freeePartnerSetting;
  show_exic_seisan_trips: boolean;
  allow_apply_past_date: boolean;
  enable_ticket_opr_auto_ticketing: boolean;
  infomart_private_cust_cd: string;
  infomart_transfer_code: string;
  exic_usage_mode: ExicUsageMode;
  allow_user_exic_input: boolean;
  api_authenticate_key: string;
  allowance_target_type: AllowanceTargetType;
  domestic_hotel_under_regulation_workflow_type: WorkflowType;
  domestic_hotel_under_regulation_buffer_amount: number;
  domestic_hotel_over_regulation_workflow_type: WorkflowType;
  domestic_hotel_over_regulation_buffer_amount: number;
  foreign_hotel_under_regulation_workflow_type: WorkflowType;
  foreign_hotel_under_regulation_buffer_amount: number;
  foreign_hotel_over_regulation_workflow_type: WorkflowType;
  foreign_hotel_over_regulation_buffer_amount: number;
  domestic_flight_workflow_type: WorkflowType;
  domestic_flight_buffer_amount: number;
  package_workflow_type: WorkflowType;
  package_buffer_amount: number;
};

/**
 * @deprecated
 * js/src/domain/organization/organization3.ts を使用すること
 */
export default class Organization {
  @observable domain: string | null | undefined;
  @observable freeeCompanies: IObservableArray<FreeeCompany>;
  @observable bulkTickets: IObservableArray<BulkTicket>;
  @observable enterpriseIdentifactionNumber: string | null | undefined;
  @observable id: number;
  @observable serviceId: number | string;
  @observable name: string;
  @observable url: string | null;
  @observable planId: number | null | undefined;
  @observable amadeusCompanyName: string | null | undefined;
  @observable address: string | undefined;
  @observable tel: string | undefined;
  @observable personInChargeName: string | null | undefined;
  @observable personInChargeEmail: string | null | undefined;
  @observable billingEmail: string | null | undefined;
  @observable bankAccountInfo: string | null | undefined;
  @observable agencyBankAccountInfo: string | null | undefined;
  @observable memberCount: number | null | undefined;
  @observable adminCount: number | null | undefined;
  @observable enableCount: number | null | undefined;
  @observable disableCount: number | null | undefined;
  @observable inviteCount: number | null | undefined;
  @observable uninviteCount: number | null | undefined;
  @observable latestPlanName: string | null | undefined;
  @observable showFee: boolean | undefined;
  @observable includeTax: boolean | undefined;
  @observable hotelSortType: boolean | 0 | 1 | undefined;
  @observable enableAutoReserveRakutenHotel: boolean | undefined;
  @observable useBulkTicket: boolean | undefined;
  @observable useKyuusyuuTicket: boolean | undefined;
  @observable shinkansenLimitType: ShinkansenLimitType;
  @observable domesticAirLimitType: DomesticAirLimitType;
  @observable rentalCarLimitType: RentalCarLimitType;
  @observable packageLimitType: PackageLimitType;
  @observable railwayPackageLimitType: RailwayPackageLimitType;
  @observable hotelLimitType: HotelLimitType;
  @observable arrangementRequestLimitType: ArrangementRequestLimitType;
  @observable showEx: boolean | null | undefined;
  @observable enabledCustomizeAddress: boolean | undefined;
  @observable useNonOrderItem: boolean | null | undefined;
  @observable requestFormApprovalRequired: boolean | null | undefined;
  @observable domesticHotelMarginType: MarginType;
  @observable domesticPackageMarginType: MarginType;
  @observable foreignMarginType: MarginType;
  @observable foreignHotelMarginType: MarginType;
  @observable domesticRentalCarMarginType: MarginType;
  @observable domesticFlightMarginType: MarginType;
  @observable domesticShinkansenMarginType: MarginType;
  @observable rakutenInnerFeeRate: number | undefined;
  @observable exicAgencyFeeRate: number | undefined;
  @observable businessEkinetCorporateInquiryNumber: string | undefined;
  @observable businessEkinetAgencyFeeRate: number | undefined;
  @observable revenueReferenceDateType!: keyof typeof Organization.revenueReferenceDateTypes;
  @observable selectedFreeeCompanyId: number | null | undefined;
  @observable freeePartnerId: number | null | undefined;
  @observable freeeMonthlyPriceSectionId: number | null | undefined;
  @observable freeeProjectDestination: string | null | undefined;
  @observable isCreateFreeeTag: boolean | null | undefined;
  @observable freeeDepartment: string | null | undefined;
  @observable organizationBillReceivers: IObservableArray<OrganizationBillReceiver>;
  @observable arrangementRequestSetting: ArrangementRequestSetting;
  @observable infomartApiToken: InfomartApiToken;
  @observable immigrationCardEnabled: boolean;
  @observable visaApplicationEnabled: boolean;
  @observable salesforceId: string | null | undefined;
  @observable salesforceImportId: string | null | undefined;
  @observable ssoEnabled: boolean | undefined;
  @observable ssoEntityId: string | null | undefined;
  @observable ssoUrl: string | null | undefined;
  @observable ssoLogoutUrl: string | null | undefined;
  @observable ssoIdpCert: string | null | undefined;
  @observable margin_types: IObservableArray<MarginType>;
  @observable members: IObservableArray<User>;
  @observable orientation: 'trip' | 'expenses';
  @observable paymentMethodType: PaymentMethodOptions;
  @observable paymentGatewayType: PaymentGatewayType | null | undefined;
  @observable invoiceDay: number | undefined | null;
  @observable invoiceSetting?: InvoiceSetting;
  @observable grades: GradeArgs[] | null | undefined;
  @observable fobiddenRepositories: FobiddenRepository[] = [];
  @observable forbiddenRequestForms: ForbiddenRequestForm[] = [];
  @observable fareOptions: FareOption[] = [];
  @observable reserveRequestMailNotificationType: ReserveRequestMailNotificationType;
  @observable freeeWorkflow: boolean | undefined;
  @observable freeeWorkflowApprovalFlowRouteIdDomestic: number | null;
  @observable freeeWorkflowFormIdDomestic: number | null;
  @observable freeeWorkflowApprovalFlowRouteIdForeign: number | null;
  @observable freeeWorkflowFormIdForeign: number | null;
  @observable transitSearchApi: TransitSearchApi;
  @observable ekispertDisplayLimited: boolean;
  @observable transportElementReservationApi: TransportElementReservationApi;
  @observable defaultShinkansenAddressType: DefaultShinkansenAddressType;
  @observable segment1: string | null | undefined;
  @observable segment2: string | null | undefined;
  @observable companySize: string | null | undefined;
  @observable setting?: Setting;
  @observable exicBasicMemberNo: string | null | undefined;
  @observable workflowStyle: workflowStyle | null | undefined;
  @observable tripReportWorkflowStyle: TripReportWorkflowStyle | null | undefined;
  @observable isOwnCompany: boolean | null | undefined;
  @observable useQr: boolean;
  @observable enableRentalcarForm: boolean;
  @observable dayOffOperation: boolean;
  @observable enableAutoStockCheck: boolean;
  @observable enableBatchApproval: boolean;
  @observable useSmartSearch: boolean;
  @observable useChatbot: boolean;
  @observable useSlackApp: boolean;
  @observable userDashboardLabel1: string | null | undefined;
  @observable userDashboardLabel2: string | null | undefined;
  @observable userDashboardLabel3: string | null | undefined;
  @observable systemFeeSetting: OrganizationSystemFeeSetting | undefined;
  @observable creditCardPaymentSettingAvailable: boolean | undefined;
  @observable enableJustBeforeReserveExpediaHotel: boolean;
  @observable enableMultipleHotelPlans: boolean;
  @observable freeePartnerSetting: freeePartnerSetting;
  @observable invoiceSplitType: InvoiceSplitType | null | undefined;
  @observable currentOptionPlanPrices?: OptionPlanPrice[];
  @observable showExicSeisanTrips: boolean;
  @observable allowApplyPastDate: boolean;
  @observable enableTicketOprAutoTicketing: boolean;
  @observable savingAccountId: number | null | undefined;
  @observable savingAccount?: SavingAccount;
  @observable allowOverdraft: boolean;
  @observable useExicReferenceNumber: boolean;
  @observable infomartPrivateCustCd: string | undefined;
  @observable infomartTransferCode: string | undefined;
  @observable exicUsageMode: ExicUsageMode;
  @observable allowUserExicInput: boolean;
  @observable apiAuthenticateKey: string;
  @observable allowanceTargetType: AllowanceTargetType;
  @observable domesticHotelUnderRegulationWorkflowType: WorkflowType;
  @observable domesticHotelUnderRegulationBufferAmount: number;
  @observable domesticHotelOverRegulationWorkflowType: WorkflowType;
  @observable domesticHotelOverRegulationBufferAmount: number;
  @observable foreignHotelUnderRegulationWorkflowType: WorkflowType;
  @observable foreignHotelUnderRegulationBufferAmount: number;
  @observable foreignHotelOverRegulationWorkflowType: WorkflowType;
  @observable foreignHotelOverRegulationBufferAmount: number;
  @observable domesticFlightWorkflowType: WorkflowType;
  @observable domesticFlightBufferAmount: number;
  @observable packageWorkflowType: WorkflowType;
  @observable packageBufferAmount: number;
  @observable concurClientId: string | null | undefined;
  @observable concurClientSecret: string | null | undefined;
  @observable concurExpenseEnabled: boolean;
  @observable concurExpenseTypeId: string | null | undefined;
  @observable concurPaymentTypeId: string | null | undefined;

  constructor(args: OrganizationArgs = {} as any) {
    ({
      id: this.id,
      service_id: this.serviceId,
      name: this.name,
      domain: this.domain,
      url: this.url,
      tel: this.tel,
      address: this.address,
      orientation: this.orientation,
      plan_id: this.planId,
      selected_freee_company_id: this.selectedFreeeCompanyId,
      freee_partner_id: this.freeePartnerId,
      freee_monthly_price_section_id: this.freeeMonthlyPriceSectionId,
      freee_project_destination: this.freeeProjectDestination,
      is_create_freee_tag: this.isCreateFreeeTag,
      freee_department: this.freeeDepartment,
      sso_enabled: this.ssoEnabled,
      sso_entity_id: this.ssoEntityId,
      sso_url: this.ssoUrl,
      sso_logout_url: this.ssoLogoutUrl,
      sso_idp_cert: this.ssoIdpCert,
      // for /admin/organizations
      amadeus_company_name: this.amadeusCompanyName,
      person_in_charge_name: this.personInChargeName,
      person_in_charge_email: this.personInChargeEmail,
      billing_email: this.billingEmail,
      bank_account_info: this.bankAccountInfo,
      agency_bank_account_info: this.agencyBankAccountInfo,
      member_count: this.memberCount,
      admin_count: this.adminCount,
      enable_count: this.enableCount,
      disable_count: this.disableCount,
      invite_count: this.inviteCount,
      uninvite_count: this.uninviteCount,
      latest_plan_name: this.latestPlanName,
      show_fee: this.showFee,
      include_tax: this.includeTax,
      hotel_sort_type: this.hotelSortType,
      enable_auto_reserve_rakuten_hotel: this.enableAutoReserveRakutenHotel,
      use_bulk_ticket: this.useBulkTicket,
      use_kyuusyuu_ticket: this.useKyuusyuuTicket,
      enabled_customize_address: this.enabledCustomizeAddress,
      show_ex: this.showEx,
      use_non_order_item: this.useNonOrderItem,
      request_form_approval_required: this.requestFormApprovalRequired,
      salesforce_id: this.salesforceId,
      salesforce_import_id: this.salesforceImportId,
      enterprise_identification_number: this.enterpriseIdentifactionNumber,
      rakuten_inner_fee_rate: this.rakutenInnerFeeRate,
      exic_agency_fee_rate: this.exicAgencyFeeRate,
      business_ekinet_corporate_inquiry_number: this.businessEkinetCorporateInquiryNumber,
      business_ekinet_agency_fee_rate: this.businessEkinetAgencyFeeRate,
      revenue_reference_date_type: this.revenueReferenceDateType,
      payment_method_type: this.paymentMethodType,
      payment_gateway_type: this.paymentGatewayType,
      invoice_day: this.invoiceDay,
      grades: this.grades,
      freee_workflow: this.freeeWorkflow,
      freee_workflow_approval_flow_route_id_domestic: this.freeeWorkflowApprovalFlowRouteIdDomestic,
      freee_workflow_form_id_domestic: this.freeeWorkflowFormIdDomestic,
      freee_workflow_approval_flow_route_id_foreign: this.freeeWorkflowApprovalFlowRouteIdForeign,
      freee_workflow_form_id_foreign: this.freeeWorkflowFormIdForeign,
      segment1: this.segment1,
      segment2: this.segment2,
      company_size: this.companySize,
      transit_search_api: this.transitSearchApi,
      ekispert_display_limited: this.ekispertDisplayLimited,
      transport_element_reservation_api: this.transportElementReservationApi,
      exic_basic_member_no: this.exicBasicMemberNo,
      workflow_style: this.workflowStyle,
      trip_report_workflow_style: this.tripReportWorkflowStyle,
      is_own_company: this.isOwnCompany,
      enable_rentalcar_form: this.enableRentalcarForm,
      day_off_operation: this.dayOffOperation,
      enable_auto_stock_check: this.enableAutoStockCheck,
      enable_batch_approval: this.enableBatchApproval,
      use_smart_search: this.useSmartSearch,
      use_chatbot: this.useChatbot,
      use_slack_app: this.useSlackApp,
      user_dashboard_label1: this.userDashboardLabel1,
      user_dashboard_label2: this.userDashboardLabel2,
      user_dashboard_label3: this.userDashboardLabel3,
      enable_just_before_reserve_expedia_hotel: this.enableJustBeforeReserveExpediaHotel,
      enable_multiple_hotel_plans: this.enableMultipleHotelPlans,
      freee_partner_setting: this.freeePartnerSetting,
      invoice_split_type: this.invoiceSplitType,
      saving_account_id: this.savingAccountId,
      infomart_private_cust_cd: this.infomartPrivateCustCd,
      infomart_transfer_code: this.infomartTransferCode,
      api_authenticate_key: this.apiAuthenticateKey,
      allowance_target_type: this.allowanceTargetType,
      domestic_hotel_under_regulation_workflow_type: this.domesticHotelUnderRegulationWorkflowType,
      domestic_hotel_under_regulation_buffer_amount: this.domesticHotelUnderRegulationBufferAmount,
      domestic_hotel_over_regulation_workflow_type: this.domesticHotelOverRegulationWorkflowType,
      domestic_hotel_over_regulation_buffer_amount: this.domesticHotelOverRegulationBufferAmount,
      foreign_hotel_under_regulation_workflow_type: this.foreignHotelUnderRegulationWorkflowType,
      foreign_hotel_under_regulation_buffer_amount: this.foreignHotelUnderRegulationBufferAmount,
      foreign_hotel_over_regulation_workflow_type: this.foreignHotelOverRegulationWorkflowType,
      foreign_hotel_over_regulation_buffer_amount: this.foreignHotelOverRegulationBufferAmount,
      domestic_flight_workflow_type: this.domesticFlightWorkflowType,
      domestic_flight_buffer_amount: this.domesticFlightBufferAmount,
      package_workflow_type: this.packageWorkflowType,
      package_buffer_amount: this.packageBufferAmount,
      concur_client_id: this.concurClientId,
      concur_client_secret: this.concurClientSecret,
      concur_expense_type_id: this.concurExpenseTypeId,
      concur_payment_type_id: this.concurPaymentTypeId
    } = args);
    this.concurExpenseEnabled = args.concur_expense_enabled ?? false;
    this.members = observable(_.map(args.members || [], m => new User(m)));
    const marginTypes = _.map(args.margin_types || [], m => new MarginType(m));
    this.domesticHotelMarginType =
      _.find(marginTypes, m => m.category === 'domestic_hotel') ||
      new MarginType({ category: 'domestic_hotel', base_amount: 0 });
    this.domesticPackageMarginType =
      _.find(marginTypes, m => m.category === 'domestic_package') ||
      new MarginType({ category: 'domestic_package', base_amount: 0 });
    this.foreignMarginType =
      _.find(marginTypes, m => m.isForeign) ||
      new MarginType({ category: 'foreign', base_type: 'fixed', base_amount: 2200 });
    this.foreignHotelMarginType =
      _.find(marginTypes, m => m.category === 'foreign_hotel') ||
      new MarginType({ category: 'foreign_hotel', base_amount: 0 });
    this.domesticRentalCarMarginType =
      _.find(marginTypes, m => m.category === 'domestic_rental_car') ||
      new MarginType({ category: 'domestic_rental_car', base_amount: 0 });
    this.domesticFlightMarginType =
      _.find(marginTypes, m => m.category === 'domestic_flight') ||
      new MarginType({ category: 'domestic_flight', base_type: 'percentage', base_amount: 5 });
    this.domesticShinkansenMarginType =
      _.find(marginTypes, m => m.category === 'domestic_shinkansen') ||
      new MarginType({ category: 'domestic_shinkansen', base_type: 'percentage', base_amount: 5 });
    this.margin_types = observable([
      this.domesticFlightMarginType,
      this.domesticShinkansenMarginType,
      this.domesticHotelMarginType,
      this.domesticPackageMarginType,
      this.foreignMarginType,
      this.foreignHotelMarginType,
      this.domesticRentalCarMarginType
    ]);
    this.bulkTickets = observable(_.map(args.bulk_tickets || [], t => new BulkTicket(t)));
    this.freeeCompanies = observable(
      args.freee_companies ? args.freee_companies.map(raw => new FreeeCompany(raw)) : []
    );
    this.organizationBillReceivers = observable(
      args.organization_bill_receivers
        ? _.map(args.organization_bill_receivers, r => new OrganizationBillReceiver(r))
        : []
    );
    this.arrangementRequestSetting = new ArrangementRequestSetting(
      args.arrangement_request_setting || ({} as any)
    );
    this.infomartApiToken = new InfomartApiToken(args.infomart_api_token || ({} as InfomartApiTokenArgs));
    this.immigrationCardEnabled = args.immigration_card_enabled || false;
    this.visaApplicationEnabled = args.visa_application_enabled || false;
    this.shinkansenLimitType = new ShinkansenLimitType(args.shinkansen_limit_type || {});
    this.domesticAirLimitType = new DomesticAirLimitType(
      args.domestic_air_limit_type || this.domesticAirLimitTypeForService()
    );
    this.rentalCarLimitType = new RentalCarLimitType(args.rental_car_limit_type || {});
    this.packageLimitType = new PackageLimitType(args.package_limit_type || this.packageLimitTypeForService());
    this.railwayPackageLimitType = new RailwayPackageLimitType(args.railway_package_limit_type || {});
    this.hotelLimitType = args.hotel_limit_type
      ? new HotelLimitType(args.hotel_limit_type)
      : HotelLimitType.newForService(this.serviceId);
    this.arrangementRequestLimitType = new ArrangementRequestLimitType(args.arrangement_request_limit_type || {});
    if (args.invoice_setting) {
      this.invoiceSetting = new InvoiceSetting(args.invoice_setting);
    }
    if (args.forbidden_repositories) {
      this.fobiddenRepositories = args.forbidden_repositories;
    }
    if (args.forbidden_request_forms) {
      this.forbiddenRequestForms = args.forbidden_request_forms;
    }
    if (args.fare_options) {
      this.fareOptions = args.fare_options.map(fareOption => ({
        optionType: fareOption.option_type,
        qualifier: fareOption.qualifier,
        identity: fareOption.identity
      }));
    }
    this.reserveRequestMailNotificationType = args.reserve_request_mail_notification_type;
    this.freeeWorkflow = args.freee_workflow || false;
    this.defaultShinkansenAddressType = args.default_shinkansen_address_type;
    if (args.setting) {
      this.setting = new Setting(args.setting);
    }
    this.transitSearchApi = args.transit_search_api;
    this.ekispertDisplayLimited = args.ekispert_display_limited || false;
    this.transportElementReservationApi = args.transport_element_reservation_api;
    this.exicBasicMemberNo = args.exic_basic_member_no;
    this.isOwnCompany = args.is_own_company || false;
    this.useQr = args.use_qr || false;
    this.useSmartSearch = args.use_smart_search || false;
    this.useChatbot = args.use_chatbot || false;
    this.useSlackApp = args.use_slack_app || false;
    this.systemFeeSetting = new OrganizationSystemFeeSetting(args.system_fee_setting || ({} as any));
    this.creditCardPaymentSettingAvailable =
      this.systemFeeSetting.paymentMethodType === 'credit_card' ||
      ((args.payment_method_type === 'card' || args.payment_method_type === 'selectable') &&
        args.payment_gateway_type === 'sony') ||
      false;
    if (args.current_option_plan_prices) {
      this.currentOptionPlanPrices = args.current_option_plan_prices.map(price => new OptionPlanPrice(price));
    }
    this.showExicSeisanTrips = args.show_exic_seisan_trips || false;
    this.allowApplyPastDate = args.allow_apply_past_date || false;
    this.enableTicketOprAutoTicketing = args.enable_ticket_opr_auto_ticketing || false;
    this.savingAccountId = args.saving_account_id;
    this.savingAccount = args.saving_account ? new SavingAccount(args.saving_account) : undefined;
    this.allowOverdraft = args.allow_overdraft || false;
    this.useExicReferenceNumber = args.use_exic_reference_number || false;
    this.infomartPrivateCustCd = args.infomart_private_cust_cd || '';
    this.infomartTransferCode = args.infomart_transfer_code || '';
    this.exicUsageMode = args.exic_usage_mode;
    this.allowUserExicInput = args.allow_user_exic_input || false;
    this.apiAuthenticateKey = args.api_authenticate_key || '';
    this.domesticHotelUnderRegulationWorkflowType =
      args.domestic_hotel_under_regulation_workflow_type || 'selected_amount';
    this.domesticHotelUnderRegulationBufferAmount = args.domestic_hotel_under_regulation_buffer_amount || 0;
    this.domesticHotelOverRegulationWorkflowType =
      args.domestic_hotel_over_regulation_workflow_type || 'selected_amount';
    this.domesticHotelOverRegulationBufferAmount = args.domestic_hotel_over_regulation_buffer_amount || 0;
    this.foreignHotelUnderRegulationWorkflowType =
      args.foreign_hotel_under_regulation_workflow_type || 'selected_amount';
    this.foreignHotelUnderRegulationBufferAmount = args.foreign_hotel_under_regulation_buffer_amount || 0;
    this.foreignHotelOverRegulationWorkflowType =
      args.foreign_hotel_over_regulation_workflow_type || 'selected_amount';
    this.foreignHotelOverRegulationBufferAmount = args.foreign_hotel_over_regulation_buffer_amount || 0;
    this.domesticFlightWorkflowType = args.domestic_flight_workflow_type || 'selected_amount';
    this.domesticFlightBufferAmount = args.domestic_flight_buffer_amount || 0;
    this.packageWorkflowType = args.package_workflow_type || 'selected_amount';
    this.packageBufferAmount = args.package_buffer_amount || 0;
  }

  static revenueReferenceDateTypes: revenueReferenceDateTypesInterface = {
    confirmed_at: '予約確定日',
    end_date: '出張完了日'
  };

  static orientations = {
    trip: '出張予約',
    expenses: '経費精算'
  };

  domesticAirLimitTypeForService() {
    if (!utils.isMynavi(this.serviceId)) return {};
    return { business_days: 1, hour: 12 };
  }

  packageLimitTypeForService() {
    if (!utils.isMynavi(this.serviceId)) return {};
    return { business_days: 7, hour: 0 };
  }

  @computed
  get revenueReferenceDateTypeText() {
    return Organization.revenueReferenceDateTypes[this.revenueReferenceDateType];
  }

  @computed
  get orientationText() {
    return Organization.orientations[this.orientation];
  }

  @computed
  get ssoIdpCertSet() {
    return !_.isEmpty(this.ssoIdpCert);
  }

  @action
  toggleShowFee() {
    this.showFee = !this.showFee;
  }

  @action
  toggleIncludeTax() {
    this.includeTax = !this.includeTax;
  }

  @action
  toggleHotelSortType() {
    this.hotelSortType = !this.hotelSortType;
  }

  @action
  toggleEnableAutoReserveRakutenHotel() {
    this.enableAutoReserveRakutenHotel = !this.enableAutoReserveRakutenHotel;
  }

  @action
  toggleUseBulkTicket() {
    this.useBulkTicket = !this.useBulkTicket;
  }

  @action
  toggleUseKyuusyuuTicket() {
    this.useKyuusyuuTicket = !this.useKyuusyuuTicket;
  }

  @action
  toggleEnabledCustomizeAddress() {
    this.enabledCustomizeAddress = !this.enabledCustomizeAddress;
  }

  @action
  toggleShowEx() {
    this.showEx = !this.showEx;
  }

  @action
  toggleUseNonOrderItem() {
    this.useNonOrderItem = !this.useNonOrderItem;
  }

  @action
  toggleRequestFormApprovalRequired() {
    this.requestFormApprovalRequired = !this.requestFormApprovalRequired;
  }

  @action
  toggleImmigrationCardEnabled() {
    this.immigrationCardEnabled = !this.immigrationCardEnabled;
  }

  @action
  toggleVisaApplicationEnabled() {
    this.visaApplicationEnabled = !this.visaApplicationEnabled;
  }

  @action
  toggleEnableRentalcarForm() {
    this.enableRentalcarForm = !this.enableRentalcarForm;
  }

  @action
  toggleDayOffOperation() {
    this.dayOffOperation = !this.dayOffOperation;
  }

  @action
  toggleEnableAutoStockCheck() {
    this.enableAutoStockCheck = !this.enableAutoStockCheck;
  }

  @action
  toggleEnableBatchApproval() {
    this.enableBatchApproval = !this.enableBatchApproval;
  }

  @action
  toggleUseSmartSearch() {
    this.useSmartSearch = !this.useSmartSearch;
  }

  @action
  toggleShowExicSeisanTrips() {
    this.showExicSeisanTrips = !this.showExicSeisanTrips;
  }

  @action
  toggleAllowApplyPastDate() {
    this.allowApplyPastDate = !this.allowApplyPastDate;
  }

  @action
  toggleEnableTicketOprAutoTicketing() {
    this.enableTicketOprAutoTicketing = !this.enableTicketOprAutoTicketing;
  }

  @action
  toggleAllowOverdraft() {
    this.allowOverdraft = !this.allowOverdraft;
  }

  @action
  toggleUseExicReferenceNumber() {
    this.useExicReferenceNumber = !this.useExicReferenceNumber;
  }

  @action
  addBillReceiver() {
    const receiver = new OrganizationBillReceiver({ organization_id: this.id });
    this.organizationBillReceivers.push(receiver);
  }

  @action
  removeBillReceiverAtIndex(index: number) {
    this.organizationBillReceivers.splice(index, 1);
  }

  @action
  toggleSsoEnabled() {
    this.ssoEnabled = !this.ssoEnabled;
  }

  @action
  toggleIsCreateFreeeTag() {
    this.isCreateFreeeTag = !this.isCreateFreeeTag;
  }

  @action
  setSsoIdpCertFile(file: File) {
    const reader = new FileReader();
    reader.onload = e => {
      this.ssoIdpCert = (e.target as FileReader).result as string;
    };
    reader.readAsDataURL(file);
  }

  @action
  setSystemFeeSettingPaymentMethodType(type: OrganizationSystemFeeSettingPaymentMethodType) {
    this.systemFeeSetting!.paymentMethodType = type;
  }

  @action
  setPaymentMethodType(type: PaymentMethodOptions) {
    this.paymentMethodType = type;
  }

  @action
  setPaymentGatewayType(type: PaymentGatewayType) {
    this.paymentGatewayType = type;
  }

  @action
  setReserveRequestMailNotificationType(type: ReserveRequestMailNotificationType) {
    this.reserveRequestMailNotificationType = type;
  }

  @action
  toggleFreeeWorkflow() {
    this.freeeWorkflow = !this.freeeWorkflow;
  }

  @action
  setTransitSearchApi(api: TransitSearchApi) {
    this.transitSearchApi = api;
  }

  @action
  setEkispertDisplayLimited(value: boolean) {
    this.ekispertDisplayLimited = value;
  }

  @action
  setTransportElementReservationApi(api: TransportElementReservationApi) {
    this.transportElementReservationApi = api;
  }

  @action
  setWorkflowStyle(style: workflowStyle) {
    this.workflowStyle = style;
  }

  @action
  setTripReportWorkflowStyle(style: TripReportWorkflowStyle) {
    this.tripReportWorkflowStyle = style;
  }

  @action
  toggleIsOwnCompany() {
    this.isOwnCompany = !this.isOwnCompany;
  }

  @action
  toggleUseChatbot() {
    this.useChatbot = !this.useChatbot;
  }

  @action
  toggleUseSlackApp() {
    this.useSlackApp = !this.useSlackApp;
  }

  @action
  toggleEnableJustBeforeReserveExpediaHotel() {
    this.enableJustBeforeReserveExpediaHotel = !this.enableJustBeforeReserveExpediaHotel;
  }

  @action
  toggleEnableMultipleHotelPlans() {
    this.enableMultipleHotelPlans = !this.enableMultipleHotelPlans;
  }

  @action
  setAutoRemoveFreeePostDeals(value: AutoRemoveFreeePostDeals) {
    this.setting!.setAutoRemoveFreeePostDeals(value);
  }

  @action
  setFreeePartnerSetting(setting: freeePartnerSetting) {
    this.freeePartnerSetting = setting;
  }

  @action
  setInvoiceSplitType(type: InvoiceSplitType) {
    this.invoiceSplitType = type;
  }

  @action
  setExicUsageMode(type: ExicUsageMode) {
    this.exicUsageMode = type;
  }

  @action
  toggleAllowUserExicInput() {
    this.allowUserExicInput = !this.allowUserExicInput;
  }

  @action
  setDomesticHotelUnderRegulationWorkflowType(type: WorkflowType) {
    this.domesticHotelUnderRegulationWorkflowType = type;
  }

  @action
  setDomesticHotelUnderRegulationBufferAmount(amount: number) {
    this.domesticHotelUnderRegulationBufferAmount = amount;
  }

  @action
  setDomesticHotelOverRegulationWorkflowType(type: WorkflowType) {
    this.domesticHotelOverRegulationWorkflowType = type;
  }

  @action
  setDomesticHotelOverRegulationBufferAmount(amount: number) {
    this.domesticHotelOverRegulationBufferAmount = amount;
  }

  @action
  setForeignHotelUnderRegulationWorkflowType(type: WorkflowType) {
    this.foreignHotelUnderRegulationWorkflowType = type;
  }

  @action
  setForeignHotelUnderRegulationBufferAmount(amount: number) {
    this.foreignHotelUnderRegulationBufferAmount = amount;
  }

  @action
  setForeignHotelOverRegulationWorkflowType(type: WorkflowType) {
    this.foreignHotelOverRegulationWorkflowType = type;
  }

  @action
  setForeignHotelOverRegulationBufferAmount(amount: number) {
    this.foreignHotelOverRegulationBufferAmount = amount;
  }

  @action
  setDomesticFlightWorkflowType(type: WorkflowType) {
    this.domesticFlightWorkflowType = type;
  }

  @action
  setDomesticFlightBufferAmount(amount: number) {
    this.domesticFlightBufferAmount = amount;
  }

  @action
  setPackageWorkflowType(type: WorkflowType) {
    this.packageWorkflowType = type;
  }

  @action
  setPackageBufferAmount(amount: number) {
    this.packageBufferAmount = amount;
  }

  @action
  setEnabledCustomizeAddress(enabled: boolean) {
    this.enabledCustomizeAddress = enabled;
  }

  @action
  setDefaultShinkansenAddressType(type: DefaultShinkansenAddressType) {
    this.defaultShinkansenAddressType = type;
  }

  getDomesticHotelBufferAmount(isOverRegulation: boolean, hotelPriceLimit: number, price: number): number {
    if (isOverRegulation) {
      return this.domesticHotelOverRegulationWorkflowType === 'with_buffer'
        ? this.domesticHotelOverRegulationBufferAmount
        : 0;
    }

    return this.domesticHotelUnderRegulationWorkflowType === 'with_buffer'
      ? this.domesticHotelUnderRegulationBufferAmount
      : this.domesticHotelUnderRegulationWorkflowType === 'price_limit'
      ? Math.max(hotelPriceLimit - price, 0)
      : 0;
  }

  getForeignHotelBufferAmount(isOverRegulation: boolean, hotelPriceLimit: number, price: number): number {
    if (isOverRegulation) {
      return this.foreignHotelOverRegulationWorkflowType === 'with_buffer'
        ? this.foreignHotelOverRegulationBufferAmount
        : 0;
    }

    return this.foreignHotelUnderRegulationWorkflowType === 'with_buffer'
      ? this.foreignHotelUnderRegulationBufferAmount
      : this.foreignHotelUnderRegulationWorkflowType === 'price_limit'
      ? Math.max(hotelPriceLimit - price, 0)
      : 0;
  }

  getDomesticFlightBufferAmount(): number {
    return this.domesticFlightWorkflowType === 'with_buffer' ? this.domesticFlightBufferAmount : 0;
  }

  getPackageBufferAmount(): number {
    return this.packageWorkflowType === 'with_buffer' ? this.packageBufferAmount : 0;
  }

  hasOptionPlan(optionPlanName: string) {
    return !!this.currentOptionPlanPrices?.some(price => price.optionPlan.name === optionPlanName);
  }

  submitParams() {
    // TODO: ソニーペイメントをprodで有効化するときにenv切り分けを削除
    const env =
      location.host.includes('localhost') || location.host.includes('ngrok.io')
        ? 'development'
        : location.host.includes('stg')
        ? 'staging'
        : 'production';

    if (env === 'production' || !utils.isAiTravel(this.serviceId)) {
      this.paymentGatewayType = 'gmo';
    }

    return {
      name: this.name,
      domain: this.domain,
      url: this.url,
      amadeus_company_name: this.amadeusCompanyName,
      address: this.address,
      tel: this.tel,
      person_in_charge_name: this.personInChargeName,
      person_in_charge_email: this.personInChargeEmail,
      billing_email: this.billingEmail,
      bank_account_info: this.bankAccountInfo,
      agency_bank_account_info: this.agencyBankAccountInfo,
      show_fee: this.showFee ? 1 : 0,
      include_tax: this.includeTax ? 1 : 0,
      hotel_sort_type: this.hotelSortType ? 1 : 0,
      enable_auto_reserve_rakuten_hotel: this.enableAutoReserveRakutenHotel ? 1 : 0,
      use_bulk_ticket: this.useBulkTicket ? 1 : 0,
      use_kyuusyuu_ticket: this.useKyuusyuuTicket ? 1 : 0,
      shinkansen_limit_type_attributes: this.shinkansenLimitType.submitParams(),
      domestic_air_limit_type_attributes: this.domesticAirLimitType.submitParams(),
      margin_types_attributes: this.margin_types.map(mt => mt.submitParams()),
      rental_car_limit_type_attributes: this.rentalCarLimitType.submitParams(),
      package_limit_type_attributes: this.packageLimitType.submitParams(),
      railway_package_limit_type_attributes: this.railwayPackageLimitType.submitParams(),
      hotel_limit_type_attributes: this.hotelLimitType.submitParams(),
      arrangement_request_limit_type_attributes: this.arrangementRequestLimitType.submitParams(),
      enabled_customize_address: this.enabledCustomizeAddress ? 1 : 0,
      show_ex: this.showEx ? 1 : 0,
      use_non_order_item: this.useNonOrderItem ? 1 : 0,
      request_form_approval_required: this.requestFormApprovalRequired ? 1 : 0,
      plan_id: this.planId,
      rakuten_inner_fee_rate: this.rakutenInnerFeeRate || 0,
      exic_agency_fee_rate: this.exicAgencyFeeRate || 0,
      business_ekinet_corporate_inquiry_number: this.businessEkinetCorporateInquiryNumber || '',
      business_ekinet_agency_fee_rate: this.businessEkinetAgencyFeeRate || 0,
      revenue_reference_date_type: this.revenueReferenceDateType,
      orientation: this.orientation,
      organization_bill_receivers_attributes: _.map(this.organizationBillReceivers, r => r.submitParams()),
      arrangement_request_setting_attributes: this.arrangementRequestSetting
        ? this.arrangementRequestSetting.submitParams()
        : undefined,
      immigration_card_enabled: this.immigrationCardEnabled,
      visa_application_enabled: this.visaApplicationEnabled,
      salesforce_id: !_.isNil(this.salesforceId) ? this.salesforceId : undefined,
      salesforce_import_id: !_.isNil(this.salesforceImportId) ? this.salesforceImportId : undefined,
      enterprise_identification_number: !_.isNil(this.enterpriseIdentifactionNumber)
        ? this.enterpriseIdentifactionNumber
        : undefined,
      setting_attributes: this.setting?.organizationSubmitParams(),
      system_fee_setting_attributes: this.systemFeeSetting?.submitParams(),
      payment_method_type: this.paymentMethodType,
      payment_gateway_type:
        this.paymentMethodType === 'card' || this.paymentMethodType === 'selectable'
          ? this.paymentGatewayType
          : null,
      invoice_day: this.invoiceDay,
      invoice_setting_attributes: this.invoiceSetting ? this.invoiceSetting.submitParams() : undefined,
      forbidden_repositories_attributes: this.fobiddenRepositories.map(repo => repo), // mobxのobservableの影響でmapで配列に変換する必要がある
      forbidden_request_forms_attributes: this.forbiddenRequestForms.map(frf => frf),
      fare_options_attributes: this.fareOptions.map(fareOption => ({
        option_type: fareOption.optionType,
        qualifier: fareOption.qualifier,
        identity: fareOption.identity
      })),
      reserve_request_mail_notification_type: this.reserveRequestMailNotificationType,
      freee_workflow: this.freeeWorkflow,
      transit_search_api: this.transitSearchApi,
      ekispert_display_limited: this.ekispertDisplayLimited,
      transport_element_reservation_api: this.transportElementReservationApi,
      default_shinkansen_address_type: this.defaultShinkansenAddressType,
      segment1: this.segment1,
      segment2: this.segment2,
      company_size: this.companySize,
      exic_basic_member_no: this.exicBasicMemberNo,
      workflow_style: this.workflowStyle,
      trip_report_workflow_style: this.tripReportWorkflowStyle,
      is_own_company: this.isOwnCompany,
      use_qr: this.useQr,
      enable_rentalcar_form: this.enableRentalcarForm ? 1 : 0,
      day_off_operation: this.dayOffOperation,
      enable_auto_stock_check: this.enableAutoStockCheck,
      enable_batch_approval: this.enableBatchApproval,
      use_smart_search: this.useSmartSearch,
      use_chatbot: this.useChatbot,
      use_slack_app: this.useSlackApp,
      user_dashboard_label1: this.userDashboardLabel1,
      user_dashboard_label2: this.userDashboardLabel2,
      user_dashboard_label3: this.userDashboardLabel3,
      enable_just_before_reserve_expedia_hotel: this.enableJustBeforeReserveExpediaHotel,
      enable_multiple_hotel_plans: this.enableMultipleHotelPlans,
      freee_partner_setting: this.freeePartnerSetting,
      invoice_split_type: this.invoiceSplitType,
      show_exic_seisan_trips: this.showExicSeisanTrips,
      allow_apply_past_date: this.allowApplyPastDate,
      enable_ticket_opr_auto_ticketing: this.enableTicketOprAutoTicketing,
      saving_account_id: this.savingAccountId || '',
      allow_overdraft: this.allowOverdraft,
      use_exic_reference_number: this.useExicReferenceNumber,
      infomart_private_cust_cd: this.infomartPrivateCustCd || '',
      infomart_transfer_code: this.infomartTransferCode || '',
      exic_usage_mode: this.exicUsageMode,
      allow_user_exic_input: this.allowUserExicInput,
      api_authenticate_key: this.apiAuthenticateKey || '',
      allowance_target_type: this.allowanceTargetType,
      domestic_hotel_under_regulation_workflow_type: this.domesticHotelUnderRegulationWorkflowType || '',
      domestic_hotel_under_regulation_buffer_amount: this.domesticHotelUnderRegulationBufferAmount || 0,
      domestic_hotel_over_regulation_workflow_type: this.domesticHotelOverRegulationWorkflowType || '',
      domestic_hotel_over_regulation_buffer_amount: this.domesticHotelOverRegulationBufferAmount || 0,
      foreign_hotel_under_regulation_workflow_type: this.foreignHotelUnderRegulationWorkflowType || '',
      foreign_hotel_under_regulation_buffer_amount: this.foreignHotelUnderRegulationBufferAmount || 0,
      foreign_hotel_over_regulation_workflow_type: this.foreignHotelOverRegulationWorkflowType || '',
      foreign_hotel_over_regulation_buffer_amount: this.foreignHotelOverRegulationBufferAmount || 0,
      domestic_flight_workflow_type: this.domesticFlightWorkflowType || '',
      domestic_flight_buffer_amount: this.domesticFlightBufferAmount || 0,
      package_workflow_type: this.packageWorkflowType || '',
      package_buffer_amount: this.packageBufferAmount || 0,
      concur_client_id: this.concurClientId || '',
      concur_client_secret: this.concurClientSecret || '',
      concur_expense_enabled: this.concurExpenseEnabled || false,
      concur_expense_type_id: this.concurExpenseTypeId || '',
      concur_payment_type_id: this.concurPaymentTypeId || ''
    };
  }
}
